import { useUrlSearchParams } from '@dai/common'
import { TabPanel } from '@dai/components'
import { Lock } from '@mui/icons-material'
import { Box, Stack, Tab, Tabs } from '@mui/material'
import { Helmet } from 'react-helmet-async'

import { CommonFilters } from '@/components/filters/filters.helpers'
import StickyHeader from '@/components/StickyHeader'
import { useTabsWithParams } from '@/hooks/use-tabs-with-params'
import useActiveSubscription from '@/hooks/useActiveSubscription'
import { useSetDefaultTimeRange } from '@/hooks/useSetDefaultTimeRange'
import useT from '@/i18n/useT'
import { Filter, FilterBar } from '@/pages/components/FilterBar'
import RevenueDetails from '@/pages/revenue/RevenueDetails'

import HomeSummary, { HomeDashboardParams } from './HomeSummary'

export type HomeTab = 'summary' | 'revenue'

const HomeTabMap: Record<HomeTab, number> = {
  summary: 0,
  revenue: 1,
}

export default function Home() {
  const { t } = useT()
  const { isPremium } = useActiveSubscription()

  const {
    state: urlState,
    handle: { handleUpdateSearchParams },
  } = useUrlSearchParams<HomeDashboardParams & { tab?: HomeTab }>()
  const { startDate, endDate } = urlState

  useSetDefaultTimeRange({
    startDate,
    endDate,
    updateSearchParams: handleUpdateSearchParams,
  })

  const filters: Filter[] = [
    {
      label: t('common.other'),
      type: 'preCannedDatePicker',
      options: { startDateKey: 'startDate', endDateKey: 'endDate' },
    },
    {
      label: t('common.startDate'),
      type: 'date',
      options: {
        urlKey: 'startDate',
      },
    },
    {
      label: t('common.endDate'),
      type: 'date',
      options: { urlKey: 'endDate' },
    },
    CommonFilters.StoreFilter(t),
  ]

  const { value, handleChange, sortedTabs } = useTabsWithParams({
    TabMap: HomeTabMap,
    tabs: [
      {
        key: 'summary',
        label: t('storeDetails.tab.summary'),
        component: <HomeSummary />,
      },
      {
        key: 'revenue',
        label: t('storeDetails.tab.revenue'),
        component: <RevenueDetails />,
      },
    ],
  })

  const renderTabLabel = (label: string, showPremiumBadge: boolean) => {
    if (!showPremiumBadge) {
      return label
    }

    return (
      <Stack direction="row" alignItems="center">
        <span>{label}</span>
        <Lock sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Stack>
    )
  }

  return (
    <>
      <Helmet title={t('homeDashboard.title')} />
      <StickyHeader>
        <FilterBar filters={filters} />
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sortedTabs.map(({ label, key }, index) => (
              <Tab
                label={renderTabLabel(label, key === 'revenue' && !isPremium)}
                id={`tab-${index}`}
                key={`tab-${index}`}
              />
            ))}
          </Tabs>
        </Box>
      </StickyHeader>

      {sortedTabs.map(({ component }, index) => (
        <TabPanel value={value} index={index} key={`tab-panel-${index}`}>
          {component}
        </TabPanel>
      ))}
    </>
  )
}
